import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Cards from 'react-credit-cards';
import { Dialog } from '@reach/dialog';
import { toast } from 'react-toastify';
import { filesize } from 'filesize';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Document, Page } from 'react-pdf';

// TODO: Replace 'react-bootstrap-daterangepicker' with a maintained alternative.
// This library is outdated and lacks proper TypeScript support. Temporary type override below.
import * as DateRangePickerLib from 'react-bootstrap-daterangepicker';
const DateRangePicker = DateRangePickerLib.default as unknown as React.FC<any>;

import ShopLogStandalone from './ShopLogStandalone';

import Accordion from '../../components/Accordion';
import AlertModal from '../../components/AlertModal';
import Chip from '../../components/Chip';
import CopyData from '../../components/CopyData';
import Counter from '../../components/Counter';
import DisplayDialog from '../../components/DisplayDialog';
import FileUpload from '../../components/FileUpload';
import GenericPopover from '../../components/GenericPopover';
import GenericTable from '../../components/GenericTable';
import HelpTooltip from '../../components/Tooltips/HelpTooltip';
import InvoicePrintView from '../../components/InvoicePrintView';
import { Led } from '../../components/Led';
import LoaderButton from '../../components/LoaderButton';
import { TextOnly } from '../../components/Text';
import TransactionsTableDialog from '../../components/TransactionsTableDialog';

import {
  downgradeShop,
  getAvalaraExemptions,
  getAvalaraZones,
  getEmailVerifiedStatus,
  getUserSummary,
  inactivateShop,
  markShopTaxExempt,
  refundTransaction,
  removeOEMFromShop,
  removeTechCertsFromShop,
  removeToolFromShop,
  removeUsersFromShop,
  setEmailAddressVerified,
  terminateShop,
  updateShopState,
  verifyShopUserId,
} from '../../libs/db-lib';
import {
  buildPdfFromElement,
  formatTimezone,
  isValidYearMonthDayDate,
  stageLabel,
} from '../../libs/utils';
import {
  createAddressInfoCard,
  createShopStateInfoCard,
  createPaymentInfoCard,
  createUserInfoCard,
} from '../../libs/ui-constructors';
import {
  fileToBase64,
  getCardIssuer,
  getCreditCardString,
} from '../../libs/utils-ts';
import {
  otherUsersColumns,
  shopCustomersColumns,
  shopEventsColumns,
  shopOrdersColumns,
  shopPartsColumns,
  shopServicesColumns,
  shopToolColumns,
} from '../../libs/tables/columnDefinitions';
import {
  CustomerVehiclesSubComponent,
  ExternalLogSubcomponent,
  LogSubcomponent,
} from '../../libs/tables/subComponents';

import { Log, QueriedUser, RefundObj, Shop, ShopOwner, ShopUser } from '../../types';
import { useUser } from '../../context/User';
import { useSystemConfig } from '../../context/SystemConfig';
import { useOemRegion } from '../../context/OemRegion';
import { UX } from '../../CONSTANTS';
import CopyLink from '../../components/CopyLink';

const maxFilesize = UX.MAX_UPLOAD_FILESIZE;

// default field values
const FIELDS = {
  taxExemptModal: {
    taxCert: null,
    taxpayerIdNumber: '',
    taxCertEffectiveDate: new Date().toISOString().slice(0, 10),
    fileError: '',
    exemptionReasons: [] as any[],
    selectedReason: { id: 0, name: '' },
    exposureZone: '',
  },
};

interface ShopInfoAccordionProps {
  shop: Shop;
  getShop: (query?: string, queryType?: string) => Promise<void>;
  queryType?: string;
  setQueryType?: (queryType: string) => void;
  setQuery?: (query: string) => void;
  isSingleShopResult: boolean;
  currentShopId?: string;
  setCurrentShopId?: (shopID: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

const ShopInfoAccordion = (props: ShopInfoAccordionProps) => {
  const {
    shop,
    getShop,
    queryType,
    setQueryType,
    setQuery,
    isSingleShopResult,
    currentShopId,
    setCurrentShopId,
    isLoading,
    setIsLoading,
  } = props;

  const { user } = useUser();
  const { config } = useSystemConfig();
  const { oemInfo } = useOemRegion();
  const location = useLocation();

  const [windowWidth, setWindowWidth] = useState<number>(window.outerWidth);

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | JSX.Element>('');

  const [showRemoveTechCertModal, setShowRemoveTechCertModal] =
    useState<boolean>(false);
  const [showTechCertRefundModal, setShowTechCertRefundModal] =
    useState<boolean>(false);
  const [numTechCertsToRemove, setNumTechCertsToRemove] = useState<number>(1);

  const [showConfirmRemoveOEMModal, setShowConfirmRemoveOEMModal] =
    useState<boolean>(false);
  const [oemToRemove, setOemToRemove] = useState<string>('');

  const [showRemoveUserModal, setShowRemoveUserModal] =
    useState<boolean>(false);
  const [numUsersToRemove, setNumUsersToRemove] = useState<number>(1);
  const [showUserRefundModal, setShowUserRefundModal] =
    useState<boolean>(false);

  const [showConfirmDowngradeModal, setShowConfirmDowngradeModal] =
    useState<boolean>(false);
  const [showConfirmInactivateModal, setShowConfirmInactivateModal] =
    useState<boolean>(false);
  const [showConfirmTerminateModal, setShowConfirmTerminateModal] =
    useState<boolean>(false);
  const [confirmShopName, setConfirmShopName] = useState<string>('');

  const [showTaxExemptModal, setShowTaxExemptModal] = useState<boolean>(false);
  const [taxCert, setTaxCert] = useState<File | null>(
    FIELDS.taxExemptModal.taxCert
  );
  const [taxpayerIdNumber, setTaxpayerIdNumber] = useState<string>(
    FIELDS.taxExemptModal.taxpayerIdNumber
  );
  const [taxCertEffectiveDate, setTaxCertEffectiveDate] = useState<string>(
    FIELDS.taxExemptModal.taxCertEffectiveDate
  );
  const [fileError, setFileError] = useState<string>(
    FIELDS.taxExemptModal.fileError
  );
  const [exemptionReasons, setExemptionReasons] = useState<any[]>(
    FIELDS.taxExemptModal.exemptionReasons
  );
  const [selectedReason, setSelectedReason] = useState<{
    id: number;
    name: string;
  }>(FIELDS.taxExemptModal.selectedReason);
  const [reasonSelections, setReasonSelections] = useState<any[]>([]);
  const [exposureZone, setExposureZone] = useState<string>(
    FIELDS.taxExemptModal.exposureZone
  );
  const [numPages, setNumPages] = useState<number>(0);

  const [showToolTableModal, setShowToolTableModal] =
    useState<boolean>(false);
  const [showUsersTableModal, setShowUsersTableModal] =
    useState<boolean>(false);
  const [showTransactionsTableModal, setShowTransactionsTableModal] =
    useState<boolean>(false);
  const [showShopOrdersTableModal, setShowShopOrdersTableModal] =
    useState<boolean>(false);
  const [showShopServicesTableModal, setShowShopServicesTableModal] =
    useState<boolean>(false);
  const [showShopPartsTableModal, setShowShopPartsTableModal] =
    useState<boolean>(false);
  const [showShopCustomersTableModal, setShowShopCustomersTableModal] =
    useState<boolean>(false);
  const [showShopEventsTableModal, setShowShopEventsTableModal] =
    useState<boolean>(false);

  const [loadingAddedBy, setLoadingAddedBy] = useState<string>('');

  const insideGetUserSummary = location.pathname.includes(
    '/userFunctions/getUserSummary'
  );
  const [shopUsers, setShopUsers] = useState<ShopUser[] | undefined>(
    shop?.shopUsers
  );

  useEffect(() => {
    setShopUsers(shop?.shopUsers);
  }, [shop]);

  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [printNumPages, setPrintNumPages] = useState<number>(0);

  const pdfModalRef = useRef<HTMLDivElement>(null);
  const iframe = useRef<HTMLIFrameElement>(null);

  const [showVerifyIdModal, setShowVerifyIdModal] = useState<boolean>(false);
  const [selectedShopUser, setSelectedShopUser] = useState<any>({});
  const [shopUserFirstName, setShopUserFirstName] = useState<string>('');
  const [shopUserLastName, setShopUserLastName] = useState<string>('');
  const [shopUserStreetAddress1, setShopUserStreetAddress1] =
    useState<string>('');
  const [shopUserStreetAddress2, setShopUserStreetAddress2] =
    useState<string>('');
  const [shopUserCity, setShopUserCity] = useState<string>('');
  const [shopUserZip, setShopUserZip] = useState<string>('');
  const [shopUserState, setShopUserState] = useState<string>('');
  const [shopUserCountry, setShopUserCountry] = useState<string>('');
  const [shopUserDocumentNumber, setShopUserDocumentNumber] =
    useState<string>('');
  const [shopUserDocumentType, setShopUserDocumentType] = useState<string>('');
  const [shopUserDocumentExpirationDate, setShopUserDocumentExpirationDate] =
    useState<string>('');

  const handleShowAlert = (
    alertTitle: string,
    alertMessage: string | JSX.Element,
  ) => {
    setAlertTitle(alertTitle);
    setAlertMessage(alertMessage);
    setShowAlertModal(true);
  };

  const handleCancelAlert = () => {
    document.querySelector('.c-modal.alert')?.classList.add('closed');
    setTimeout(() => {
      setShowAlertModal(false);
    }, 350);
  }

  const handleSetWindowWidth = () => {
    setTimeout(() => {
      setWindowWidth(window.outerWidth);
    }, 20);
  };

  useEffect(() => {
    window.addEventListener('resize', handleSetWindowWidth);

    return () => {
      window.removeEventListener('resize', handleSetWindowWidth);
    };
  }, []);

  if (!user) return null;

  const isAdvancedRole = ['MANAGER', 'ADMIN'].includes(user.userType);
  const isAdmin = ['ADMIN'].includes(user.userType);
  const isNotExternal = user.userType !== 'EXTERNAL';

  const baseTechCerts =
    config[`${shop.shopType?.toLowerCase()}BaseTechCerts`] ?? 0;
  const maxTechCerts = shop.shopMaxTechCerts ?? 0;
  const numUsedTechCerts = shop.numUsedTechCerts ?? 0;
  const unusedPurchasedTechCerts =
    numUsedTechCerts <= baseTechCerts
      ? maxTechCerts - baseTechCerts
      : maxTechCerts - numUsedTechCerts;
  const techCertsEligibleForRefund =
    numTechCertsToRemove <= unusedPurchasedTechCerts
      ? numTechCertsToRemove
      : unusedPurchasedTechCerts;

  const baseUsers = config[`${shop.shopType?.toLowerCase()}BaseUsers`];
  const maxUsersTools = shop?.shopMaxUsers;
  const purchasedUsersTools = maxUsersTools - baseUsers;
  const unusedPurchasedUsers = purchasedUsersTools ? maxUsersTools - (shopUsers?.length ?? 0) : 0;
  const unusedPurchasedTools = purchasedUsersTools ? maxUsersTools - shop?.shopTools?.length : 0;
  const unusedPurchasedUsersTools = (shopUsers?.length ?? 0) > shop?.shopTools?.length
    ? unusedPurchasedUsers : unusedPurchasedTools;

  const handleConfirmShopNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmShopName(event.target.value);
  };

  const handleShowRemoveTechCertModal = () => {
    setShowRemoveTechCertModal(true);
  };

  const handleHideRemoveTechCertModal = () => {
    if (isLoading) return;

    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowRemoveTechCertModal(false);
    }, 350);
  };

  const handleHideTechCertRefundModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowTechCertRefundModal(false);
      setNumTechCertsToRemove(1);
    }, 350);
  };

  const handleRemoveTechCerts = async (shopID: string, numToRemove: number) => {
    setIsLoading(true);

    const response: any = await removeTechCertsFromShop(shopID, numToRemove);
    if (!response?.error) {
      await getShop();
      setShowTechCertRefundModal(true);
    } else {
      handleShowAlert(
        'Error',
        'Tech Cert(s) could not be removed: ' + response.error
      );
    }

    setIsLoading(false);
    setConfirmShopName('');
    handleHideRemoveTechCertModal();
  };

  const handleShowRemoveOEMModal = (oemID: string) => {
    setShowConfirmRemoveOEMModal(true);
    setOemToRemove(oemID);
  };

  const handleHideRemoveOEMModal = () => {
    if (isLoading) return;

    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowConfirmRemoveOEMModal(false);
    }, 350);
  };

  const handleRemoveOEM = async (shopID: string, oemID: string) => {
    setIsLoading(true);

    try {
      const result = await removeOEMFromShop(shopID, oemID);

      if (result.success) {
        const transRec = shop.transactions?.find((trans) => {
          return (
            (trans.type === 'upgradeOEMRegionProration' &&
              trans.addedOems?.includes(oemID)) ||
            (['initialSubscriptionCharge', 'subscriptionRenew'].includes(
              trans.type
            ) &&
              trans.oemIDs?.includes(oemID))
          );
        });

        if (transRec) {
          const milliPerDay = 1000 * 3600 * 24;
          let amount = [
            'initialSubscriptionCharge',
            'subscriptionRenew',
          ].includes(transRec.type)
            ? config.prices.oemRegionAddon
            : (transRec?.addedOems ?? []).length === 1
            ? transRec.subTotal || Number(transRec.amount)
            : (transRec.subTotal || Number(transRec.amount)) /
              (transRec?.addedOems ?? []).length;
          if (transRec.taxAmount && transRec.taxAmount > 0) {
            amount +=
              (amount / (transRec.subTotal || Number(transRec.amount))) *
              transRec.taxAmount;
          }
          const transDay = new Date(transRec.time).getTime();
          const today = Date.now();
          const timeSinceTransaction = Math.ceil(
            (today - transDay) / milliPerDay
          );
          let refundAmount;
          if (timeSinceTransaction >= 14) {
            const endDay = new Date(
              shop.shopPaymentInfo.nextBillingDate
            ).getTime();
            const totalLenDays = Math.ceil((endDay - transDay) / milliPerDay);
            const pricePerDay = Math.floor((amount * 100) / totalLenDays) / 100;
            const leftDays = Math.ceil((endDay - today) / milliPerDay);
            refundAmount = Math.floor(leftDays * pricePerDay * 100) / 100;
          } else {
            refundAmount = Math.floor(amount * 100) / 100;
            console.log('refundAmount: ', refundAmount);
          }

          // Only refund if the transaction found is less than 6 months ago
          if (timeSinceTransaction < 6 * 30) {
            const refundDetails: RefundObj = {
              oemIDRefunded: oemID,
            };
            if (transRec.status !== 'voided') {
              const refundResponse = await refundTransaction(
                transRec.id,
                shop.shopID,
                refundAmount,
                false,
                refundDetails
              );
              if (refundResponse.success) {
                handleShowAlert('Success', refundResponse.success);
              } else {
                handleShowAlert('Error', refundResponse.error);
              }
            }
          }
        }

        await getShop();
      }
    } catch (e) {
      handleShowAlert('Error', 'OEM could not be removed: ' + e);
    } finally {
      setIsLoading(false);
      setConfirmShopName('');
      handleHideRemoveOEMModal();
    }
  };

  const handleShowRemoveUserModal = () => {
    setShowRemoveUserModal(true);
  };

  const handleHideRemoveUserModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowRemoveUserModal(false);
    }, 350);
  };

  const handleHideUserRefundModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowUserRefundModal(false);
      setNumUsersToRemove(1);
    }, 350);
  };

  const handleRemoveUsers = async (shopID: string, numToRemove: number) => {
    setIsLoading(true);

    const response: any = await removeUsersFromShop(shopID, numToRemove);
    if (!response?.error) {
      await getShop();
      setShowUserRefundModal(true);
    } else {
      handleShowAlert(
        'Error',
        'User/Tool(s) could not be removed: ' + response.error
      );
    }

    setIsLoading(false);
    setConfirmShopName('');
    handleHideRemoveUserModal();
  };

  const handleLogData = (logData: Log[]) => {
    shop.shopLog = logData;
  };

  const handleQueryShopID = () => {
    if (insideGetUserSummary) {
      return;
    } else {
      setQuery?.(shop.shopID);
      setQueryType?.('SHOP ID');
      getShop(shop.shopID, 'SHOP ID');
    }
  };

  // Adds the technician userName to each order
  // instead of only having the technicianID
  const addTechnicianUsername = (shopToUpdate: Shop) => {
    const shopUsers = shopToUpdate.shopUsers;

    shopToUpdate.crmShopOrders = shopToUpdate.crmShopOrders?.map((order) => {
      order.userName = shopUsers.find((user) => {
        return user.userID === order.technicianID;
      })?.userName;

      return order;
    });

    return shopToUpdate;
  };

  // Adds the customer name to each order
  // instead of only having the customerID
  const addCustomerName = (shopToUpdate: Shop) => {
    const shopCustomers = shopToUpdate.crmShopCustomers;

    shopToUpdate.crmShopOrders = shopToUpdate.crmShopOrders?.map((order) => {
      const currentCustomer = shopCustomers?.find((customer) => {
        return customer.customerID === order.customerID;
      });

      order.customerName = `${currentCustomer?.firstName} ${currentCustomer?.lastName}`;
      return order;
    });

    return shopToUpdate;
  };

  const handleShowTableModal = (modal: string, shopToShow: Shop) => {
    switch (modal) {
      case 'tools':
        setShowToolTableModal(true);
        break;
      case 'users':
        setShowUsersTableModal(true);
        break;
      case 'transactions':
        setShowTransactionsTableModal(true);
        break;
      case 'shopOrders':
        setShowShopOrdersTableModal(true);
        // Put technician and customer names into the shop orders
        addTechnicianUsername(shopToShow);
        addCustomerName(shopToShow);
        break;
      case 'shopServices':
        setShowShopServicesTableModal(true);
        break;
      case 'shopParts':
        setShowShopPartsTableModal(true);
        break;
      case 'shopCustomers':
        setShowShopCustomersTableModal(true);
        break;
      case 'shopEvents':
        setShowShopEventsTableModal(true);
      default:
        break;
    }
  };

  const handleHideTableModal = () => {
    document.querySelector('.c-modal-slider-75')?.classList.add('closed');
    document.querySelector('.c-modal-slider-90')?.classList.add('closed');
    setTimeout(() => {
      setShowToolTableModal(false);
      setShowUsersTableModal(false);
      setShowTransactionsTableModal(false);
      setShowShopOrdersTableModal(false);
      setShowShopServicesTableModal(false);
      setShowShopPartsTableModal(false);
      setShowShopCustomersTableModal(false);
      setShowShopEventsTableModal(false);
    }, 350);
  };

  // Clicking a nav link in a modal (e.g., a username in the Shop Users modal)
  // updates the URL params but may leave the modal open when navigating to the
  // same route, hiding the new results. Close the modal on navigation so the
  // updated results are visible.
  const prevLocationRef = useRef(location);
  useEffect(() => {
    if (insideGetUserSummary && prevLocationRef.current !== location) {
      prevLocationRef.current = location;
      handleHideTableModal();
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 350);
    }
  }, [location]);

  const handleRemoveTool = async (shopID: string, toolID: string) => {
    try {
      await removeToolFromShop(shopID, toolID);

      shop.shopTools = shop.shopTools?.filter(
        (tool) => tool.toolID !== toolID
      );
      shop.shopToolsCount = shop.shopToolsCount - 1;

      handleShowAlert('Success', 'Tool has been removed!');
    } catch (e) {
      if (Object.hasOwn(e, 'error')) {
        if ([401, 403].indexOf(e.error.status) === -1) {
          handleShowAlert('Error', 'Tool could not be removed: ' + e);
        }
        return;
      }
    }
  };

  const handleRefundTransaction = async (
    transactionID: string,
    shopID: string,
    amount: string,
    taxOnly: boolean,
    refundDetails?: RefundObj
  ) => {
    const response = await refundTransaction(
      transactionID,
      shopID,
      amount,
      taxOnly,
      refundDetails
    );

    if (response.success) {
      handleShowAlert('Success', response.success);
      const newTransaction = response.result;
      const updatedShopTransactions = shop.transactions?.map(
        (transaction) => {
          if (transaction.id === transactionID) {
            const updatedTransaction = { ...transaction };
            if (transactionID === newTransaction.id) {
              updatedTransaction.status = newTransaction.status;
            } else {
              updatedTransaction.refundedTo = newTransaction.id;
            }
            updatedTransaction.refundDetails = refundDetails;
            return updatedTransaction;
          }
          return transaction;
        }
      );

      if (transactionID !== newTransaction.id) {
        updatedShopTransactions?.unshift(newTransaction);
      }

      shop.transactions = updatedShopTransactions;
      shop.transactionsCount = updatedShopTransactions.length;
    } else {
      handleShowAlert('Error', response.error);
    }
  };

  const downloadPdf = (orderData: any): string => {
    const filename = `${stageLabel(orderData) + orderData.orderNumber}`;

    const options = {
      margin: [0.15, 0, 0, 0],
      filename: filename,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: [8.5, 11], orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    return buildPdfFromElement(`print-element-${orderData?.orderID}`, options);
  };

  const onPrintDocumentLoadSuccess = ({
    numPages: loadedNumPages,
  }: {
    numPages: number;
  }) => {
    setPrintNumPages(loadedNumPages);
  };

  const openPrintModal = async (orderData: any) => {
    const blobUrl = await downloadPdf(orderData);
    document.querySelector('.c-modal')?.classList.remove('closed');
    setShowPrintModal(true);
    setPdfUrl(blobUrl);
  };

  const closePrintModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowPrintModal(false);
      setPdfUrl('');
    }, 350);
  };

  const checkEmailVerified = async (queriedUser: QueriedUser) => {
    const status = await getEmailVerifiedStatus(queriedUser.userName);
    const newUsers = (shop?.shopUsers ?? []).map((u) => {
      if (u.userID === queriedUser.userID) {
        u.emailVerified = status.verified;
      }
      return u;
    });
    shop.shopUsers = newUsers;
    setShopUsers(newUsers);
  };

  const verifyEmailAddress = async (queriedUser: QueriedUser) => {
    setIsLoading(true);
    const status = await setEmailAddressVerified(queriedUser.userName);
    if (status.error) {
      toast.error(`${status.error}`);
    } else {
      const newUsers = (shop?.shopUsers ?? []).map((u) => {
        if (u.userID === queriedUser.userID) {
          u.emailVerified = true;
        }
        return u;
      })
      shop.shopUsers = newUsers;
      setShopUsers(newUsers);
    }
    setIsLoading(false);
  };

  const handleVerifyIdButtonClick = (shopUser: ShopUser) => {
    setShowVerifyIdModal(true);
    setSelectedShopUser({ ...shopUser });
  };

  const handleVerifyId = async () => {
    setIsLoading(true);

    const shopUserIdInfo = {
      firstName: shopUserFirstName,
      lastName: shopUserLastName,
      streetAddress1: shopUserStreetAddress1,
      streetAddress2: shopUserStreetAddress2,
      city: shopUserCity,
      zip: shopUserZip,
      state: shopUserState,
      country: shopUserCountry,
      documentNumber: shopUserDocumentNumber,
      documentType: shopUserDocumentType,
      expirationDate: shopUserDocumentExpirationDate,
    };

    const response: any = await verifyShopUserId(
      shop?.shopID,
      selectedShopUser?.userID,
      shopUserIdInfo
    );

    if (!response?.error) {
      await getShop();
    } else {
      handleShowAlert('Error', `Error Verifying User's Id: ${response.error}`);
    }

    setIsLoading(false);
    closeVerifyIdModal();
    handleHideTableModal();
    handleShowAlert('Success', `Successfully Verified User's Id!`);
  };

  const closeVerifyIdModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowVerifyIdModal(false);
      setSelectedShopUser({});
      setShopUserFirstName('');
      setShopUserLastName('');
      setShopUserStreetAddress1('');
      setShopUserStreetAddress2('');
      setShopUserCity('');
      setShopUserZip('');
      setShopUserState('');
      setShopUserCountry('');
      setShopUserDocumentNumber('');
      setShopUserDocumentType('');
      setShopUserDocumentExpirationDate('');
    }, 350);
  };

  const handleUpdateShopState = async (shopID: string, state: string) => {
    setIsLoading(true);

    try {
      let result = await updateShopState(shopID, state);
      if (Object.hasOwn(result, 'error')) {
        if ([401, 403].indexOf(result.error.status) !== -1) {
          return;
        }
      }
      handleShowAlert(
        'Success',
        `Shop has been ${state === 'SUSPENDED' ? 'suspended!' : 'reinstated!'}`
      );

      shop.shopState = state;
    } catch (e) {
      handleShowAlert('Error', 'Shop could not be updated: ' + e);
    } finally {
      setIsLoading(false);
    }
  };

  function isNotDowngradable(shop: Shop): boolean {
    return !!(
      ((shopUsers?.length ?? 0) && (shopUsers?.length ?? 0) > 5) ||
      (shop?.shopTools?.length && shop.shopTools.length > 5) ||
      !shop?.shopPaymentInfo?.subscriptionId ||
      shop?.shopType !== 'PLUS' ||
      shop?.shopSubscriptionState !== 'VALID'
    );
  }

  const handleShowDowngradeModal = (shop: Shop) => {
    if (isNotDowngradable(shop)) {
      alert(
        "Ineligible. Shop has over 5 Users/Tools, it's passed due or it's not an active subscription"
      );
      return;
    }
    setShowConfirmDowngradeModal(true);
  };

  const handleHideDowngradeModal = () => {
    if (isLoading) return;

    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowConfirmDowngradeModal(false);
    }, 350);
  };

  const handleDowngradeShop = async (shopID: string) => {
    setIsLoading(true);

    try {
      const result = await downgradeShop(shopID);
      if (result.success) {
        const transRef = shop.transactions?.find((trans) =>
          [
            'upgradePlusProration',
            'initialSubscriptionCharge',
            'subscriptionRenew',
          ].includes(trans.type)
        );

        const milliToDays = 1 / (1000 * 3600 * 24);
        const amount = [
          'initialSubscriptionCharge',
          'subscriptionRenew',
        ].includes(transRef?.type as string)
          ? 150
          : Number(transRef?.amount);
        const startPay = new Date(transRef?.time as string).getTime();
        const today = Date.now();
        const timeSinceTransaction = Math.ceil(
          (today - startPay) * milliToDays
        );
        let amountRef;
        if (timeSinceTransaction >= 14) {
          const endPay = new Date(
            shop.shopPaymentInfo.nextBillingDate
          ).getTime();
          const totalLenDays = Math.ceil((endPay - startPay) * milliToDays);
          const baseDay = Math.floor((amount * 100) / totalLenDays) / 100;
          const leftDays = Math.ceil((endPay - today) * milliToDays);
          amountRef = Math.floor(leftDays * baseDay * 100) / 100;
        } else {
          amountRef = amount;
        }

        // Only refund if the transaction found is less than 6 months ago
        if (timeSinceTransaction < 6 * 30) {
          const refundDetails: RefundObj = {
            plusUpgradeRefunded: true,
          };
          if (transRef?.status !== 'voided') {
            const refundResponse = await refundTransaction(
              transRef?.id as string,
              shop.shopID,
              amountRef.toString(),
              false,
              refundDetails
            );
            if (refundResponse.success) {
              handleShowAlert('Success', refundResponse.success);
            } else {
              handleShowAlert('Error', refundResponse.error);
            }
          }
        }

        await getShop();
      }
    } catch (e) {
      handleShowAlert('Error', 'Shop could not be downgraded: ' + e);
    } finally {
      handleHideDowngradeModal();
      setIsLoading(false);
      setConfirmShopName('');
    }
  };

  const handleShowInactivateModal = () => {
    setShowConfirmInactivateModal(true);
  };

  const handleHideInactivateModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowConfirmInactivateModal(false);
      setConfirmShopName('');
    }, 350);
  };

  const handleInactivateShop = async (shopID: string) => {
    setIsLoading(true);

    try {
      const result = await inactivateShop(shopID);
      if (result.success) {
        handleShowAlert('Success', 'Shop has been successfully inactivated!');
        await getShop();
      }
    } catch (e) {
      handleShowAlert('Error', 'Shop could not be inactivated: ' + e);
    } finally {
      setIsLoading(false);
      setConfirmShopName('');
      handleHideInactivateModal();
    }
  };

  const handleShowTerminateModal = () => {
    setShowConfirmTerminateModal(true);
  };

  const handleHideTerminateModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowConfirmTerminateModal(false);
    }, 350);
  };

  const handleTerminateShop = async (shopID: string) => {
    setIsLoading(true);

    try {
      const result = await terminateShop(shopID);
      if (Object.hasOwn(result, 'error')) {
        if ([401, 403].indexOf(result.error.status) !== -1) {
          return;
        }
      }

      handleShowAlert('Success', 'Shop has been terminated!');
      await getShop();
    } catch (e) {
      handleShowAlert('Error', 'Shop could not be terminated: ' + e);
    } finally {
      setIsLoading(false);
      setConfirmShopName('');
      handleHideTerminateModal();
    }
  };

  const handleShowTaxExemptModal = async (shop: Shop) => {
    setShowTaxExemptModal(true);

    getAvalaraExemptions().then((res) => {
      if (!res.error) {
        setExemptionReasons(res);
      } else {
        handleHideTaxExemptModal();
        handleShowAlert(
          `Mark Shop Tax Exempt`,
          'There was an error retrieving Avalara exemption reasons.'
        );
      }
    });

    getAvalaraZones().then((res) => {
      if (!res.error) {
        // derive full exposure zone name from the shop's state abbreviation
        const region = shop.addressInfo.state;
        const exposureZone = res.find((zone) => zone.region === region)?.name || null;

        setExposureZone(exposureZone);
      } else {
        handleHideTaxExemptModal();
        handleShowAlert(
          `Mark Shop Tax Exempt`,
          'There was an error retrieving Avalara exposure zones.'
        );
      }
    });
  };

  const handleHideTaxExemptModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');

    const {
      taxCert,
      taxpayerIdNumber,
      taxCertEffectiveDate,
      fileError,
      selectedReason,
      exposureZone,
    } = FIELDS.taxExemptModal;

    setTaxCert(taxCert);
    setTaxpayerIdNumber(taxpayerIdNumber);
    setTaxCertEffectiveDate(taxCertEffectiveDate);
    setFileError(fileError);
    setSelectedReason(selectedReason);
    setExposureZone(exposureZone);
    setReasonSelections([]);

    setTimeout(() => {
      setShowTaxExemptModal(false);
    }, 350);
  };

  const isMissingInfo = () => {
    const hasTaxId = taxpayerIdNumber;
    const hasTaxCert = taxCert;
    const hasEffDate = taxCertEffectiveDate;
    const hasZone = exposureZone;
    const hasReason = selectedReason && selectedReason.name.length !== 0;

    return !(hasTaxId && hasTaxCert && hasEffDate && hasReason && hasZone);
  };

  const handleMarkShopTaxExempt = async (
    shopID: string,
    taxpayerIdNumber: string,
    taxCert: File | null,
    taxCertEffectiveDate: string,
    exemptionReason: string,
    exposureZone: string,
    attempts: number
  ): Promise<void> => {
    setIsLoading(true);

    const taxCertBase64 = taxCert ? await fileToBase64(taxCert) : null;
    const response: any = await markShopTaxExempt(
      shopID,
      taxpayerIdNumber,
      taxCertBase64,
      taxCertEffectiveDate,
      exemptionReason,
      exposureZone
    );

    // normalize "error" into a safe shape so we can handle:
    // - response.error as string
    // - response.error as object (with nested data/message)
    // - missing/unknown shapes
    const errorVal = response?.error;

    const errorText =
      typeof errorVal === 'string'
        ? errorVal
        : errorVal?.data?.message ??
          errorVal?.message ??
          response?.data?.message ??
          response?.message ??
          null;

    const normalizedError = errorText ?? errorVal;
    const isCertCreationError =
      normalizedError === 'Certificates API returned error';
    const customerExists =
      normalizedError === 'The object is a duplicate of an existing object.';

    if (!errorVal) {
      await getShop();

      // if we had to retry because of a reason/region mismatch error,
      // then let the user know that we used 'OTHER/CUSTOM' instead
      let msg = '';
      if (attempts < UX.MAX_AVALARA_RETRY_ATTEMPTS) {
        const reason = selectedReason;
        const exemptionReason = reason.name;

        msg = ` Could not use ${exemptionReason}. Used 'OTHER/CUSTOM' instead.`;
      }

      handleShowAlert(
        'Success',
        `Shop has been successfully marked tax exempt.${msg}`
      );
    } else if (isCertCreationError && attempts) {
      console.log('retry, but fall back to OTHER/CUSTOM as the exemption reason');
      await handleMarkShopTaxExempt(
        shopID,
        taxpayerIdNumber,
        taxCert,
        taxCertEffectiveDate,
        'OTHER/CUSTOM',
        exposureZone,
        attempts - 1
      );
    } else if (customerExists) {
      console.log('avalara customer record for this shop already exists');
    } else {
      const message = errorText || 'Unknown error';

      handleShowAlert(
        'Error',
        `Could not mark shop Tax Exempt: ${message}`
      );
    }

    setIsLoading(false);
    setConfirmShopName('');
    handleHideTaxExemptModal();
  };

  const displayMaxFilesize = () => {
    return filesize(maxFilesize, { round: 0 });
  };

  const attachTaxCert = (file: File | null) => {
    if (file !== null) {
      const taxCert = file[0];
      const { size, type } = taxCert;
      const isTooBig = size > maxFilesize;
      const isPDF = type === 'application/pdf';

      if (!isTooBig && isPDF) {
        setTaxCert(taxCert);
        setFileError('');
      } else {
        setTaxCert(null);

        if (isTooBig) {
          setFileError(
            `This file is too big. Please upload a file under ${displayMaxFilesize()}.`
          );
        }

        if (!isPDF) {
          setFileError('Incorrect file format. Please upload a PDF.');
        }
      }
    }
  };

  const clearTaxCert = () => {
    setTaxCert(null);
    setFileError('');
  };

  const taxCertInfo = () => {
    let info;

    if (taxCert) {
      let { name, size } = taxCert;

      info = (
        <span>
          {`${name}`}&nbsp;
          <span className="u-font-size-small">
            [ <em>{`${filesize(size)}`}</em> ]&nbsp;
          </span>
          <a
            className="u-font-size-small"
            href="#"
            onClick={() => clearTaxCert()}
          >
            clear
          </a>
        </span>
      );
    }

    return info;
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  let shopStateCard = createShopStateInfoCard(
    shop,
    windowWidth,
    isAdmin,
    handleShowAlert
  );

  let crmStateCard = createShopStateInfoCard(
    shop,
    windowWidth,
    isAdmin,
    handleShowAlert,
    true // isCRMCard
  );

  let addressInfoCard = createAddressInfoCard(shop, windowWidth);

  let paymentInfoCard = createPaymentInfoCard(shop, windowWidth, config, isAdvancedRole);

  let crmPaymentInfoCard = createPaymentInfoCard(
    shop,
    windowWidth,
    config,
    isAdvancedRole,
    true // isCRMCard
  );

  let transactionsCount =
    shop.transactionsCount || shop.transactions?.length || 0;

  let transactionsCard =
    windowWidth < 1220 ? (
      <div className="u-margin-bottom-large">
        <label className="mobile-display-label">Transactions</label>
        {transactionsCount > 0 ? (
          <div className="l-flex-align-stretch">
            <div className="mobile-display-value-with-button">
              {transactionsCount}{' '}
            </div>
            <div
              className="mobile-display-value-button"
              onClick={() =>
                handleShowTableModal('transactions', shop)
              }
            >
              <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
            </div>
          </div>
        ) : (
          transactionsCount
        )}
      </div>
    ) : (
      <tr className="card-tr">
        <th className="card-th">Transactions:</th>
        <td className="card-td">
          {transactionsCount > 0 ? (
            <div className={'l-flex-align-center'}>
              {transactionsCount}{' '}
              <button
                className="c-btn-icon"
                onClick={() =>
                  handleShowTableModal('transactions', shop)
                }
              >
                <div className="c-btn__inner">
                  <i className="c-btn__icon fa fa-chevron-right" />
                </div>
              </button>
            </div>
          ) : (
            transactionsCount
          )}
        </td>
      </tr>
    );

  let shopOwnerInfoKeymap = {
    ID: 'ID',
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
  };

  let shopOwnerInfoCard = Object.keys(shopOwnerInfoKeymap).map((key) => {
    let value = shop.shopOwner
      ? shop.shopOwner[key as keyof ShopOwner]
      : null;
    return value ? (
      <tr className="card-tr" key={key}>
        <th className="card-th card-table-col-labels-20">
          {shopOwnerInfoKeymap[key as keyof Object] + ': '}
        </th>
        <td className="card-td u-font-mono">{value}</td>
      </tr>
    ) : null;
  });

  const suspended = shop.shopState === 'SUSPENDED';
  const terminated = shop.shopState === 'TERMINATED';
  const inactive = shop.shopState === 'INACTIVE';

  const taxExemptExists = typeof shop?.taxExempt === 'boolean';
  const taxAmountExists = typeof shop?.shopPaymentInfo?.taxAmount === 'number';
  const taxAmount = shop?.shopPaymentInfo?.taxAmount || 0; // Needed to supress TS linter error

  let taxExemptStatus;

  if (taxExemptExists) {
    taxExemptStatus = shop.taxExempt ? 'Yes' : 'No';
  } else if (!taxAmountExists) {
    taxExemptStatus = 'No';
  } else if (taxAmount > 0) {
    taxExemptStatus = 'No';
  } else {
    taxExemptStatus = 'Unknown';
  }

  const showTaxExemptButton = isAdmin && (taxExemptStatus === 'No' || taxExemptStatus === 'Unknown');
  const disableTaxExemptButton = !shop.addressInfo.state;

  const userTableColumns = otherUsersColumns(
    user.userTimezone,
    checkEmailVerified,
    verifyEmailAddress
  ).filter(
    (column) => !(user.userType === 'EXTERNAL' && column.Header === 'User ID')
  );

  return (
    <Accordion
      title={'Shop Name: ' + shop.shopName}
      key={shop.shopID}
      shopID={shop.shopID}
      collapse={currentShopId}
      accordionToggled={() => setCurrentShopId?.('')}
      icon={<span className="material-icons">store</span>}
    >
      {insideGetUserSummary && 
        <>
          {windowWidth > 1220 && (
            <div className="c-card u-margin-bottom">
              <h2 className="c-card__title">Basic User Info</h2>
              <div className="c-card__description">
                <table className="card-table">
                  <colgroup>
                    <col />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr className="card-tr" key="2">
                      <th className="card-th">User's Shop Role: </th>
                      <td className="card-td">{shop?.shopUserRole}</td>
                      <th className="card-th">User's Shop State: </th>
                      <td className="card-td">{shop?.shopUserState}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {windowWidth <= 1220 && (
            <div className="c-card u-margin-bottom">
              <h2 className="c-card__title">Basic User Info</h2>
              <div className="c-card__description">
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">User's Shop Role:</label>
                  <div className="mobile-display-value u-font-mono">
                    {shop?.shopUserRole}
                  </div>
                </div>

                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    User's Shop State:
                  </label>
                  <div className="mobile-display-value">
                    {shop?.shopUserState}
                  </div>
                </div>
              </div>
            </div>
          )}  
        </>
      }

      {/* Basic Info */}
      {isAdvancedRole && (
        <div className="l-flex-around-center-wrap">
          <div className="l-flex-end  u-margin-bottom">
            {suspended ? (
              <GenericPopover
                anchorElement={
                  <button
                    className="c-btn-medium c-btn-success"
                    onClick={() => setCurrentShopId?.(shop.shopID)}
                  >
                    <div className="c-btn__inner">
                      <i className="c-btn__icon fa fa-play" />
                      Reinstate
                    </div>
                  </button>
                }
                body={
                  <strong>
                    Are you sure you want to reinstate this shop?
                  </strong>
                }
                primaryBtnElement={
                  <LoaderButton
                    className="c-btn-medium c-btn-success"
                    disabled={false}
                    text="Yes, reinstate it"
                    loadingText="Reinstating..."
                    icon={'fa fa-play'}
                  />
                }
                secondaryBtnElement={
                  <button className="c-btn-medium c-btn-outline">
                    <div className="c-btn__inner">
                      <i className="c-btn__icon fal fa-times" />
                      No
                    </div>
                  </button>
                }
                primaryAction={async () => {
                  return handleUpdateShopState(
                    shop.shopID,
                    'ACTIVE'
                  );
                }}
                color="#fff"
                position="top"
                align="end"
                arrowSize={5}
                closeOnClick={false}
              />
            ) : (
              <GenericPopover
                anchorElement={
                  <div>
                    {!terminated ? (
                      <button
                        className="c-btn-medium c-btn-warning"
                        disabled={inactive}
                        onClick={() => setCurrentShopId?.(shop.shopID)}
                      >
                        <div className="c-btn__inner">
                          <i className="c-btn__icon fa fa-pause" />
                          Suspend
                        </div>
                      </button>
                    ) : (
                      ' '
                    )}
                  </div>
                }
                body={
                  <strong>
                    Are you sure you want to suspend this shop?
                  </strong>
                }
                primaryBtnElement={
                  <LoaderButton
                    className="c-btn-medium c-btn-warning"
                    disabled={false}
                    text="Yes, suspend it"
                    loadingText="Suspending..."
                    icon={'fa fa-pause'}
                  />
                }
                secondaryBtnElement={
                  <button className="c-btn-medium c-btn-outline">
                    <div className="c-btn__inner">
                      <i className="c-btn__icon fal fa-times" />
                      No
                    </div>
                  </button>
                }
                primaryAction={async () => {
                  return handleUpdateShopState(
                    shop.shopID,
                    'SUSPENDED'
                  );
                }}
                color="#fff"
                position="top"
                align="end"
                arrowSize={5}
                closeOnClick={false}
              />
            )}
          </div>
          {!terminated ? (
            <div className="l-flex-end u-margin-bottom">
              <button
                className="c-btn-medium c-btn-error"
                disabled={inactive}
                onClick={handleShowTerminateModal}
              >
                <div className="c-btn__inner">
                  <i className="c-btn__icon fa fa-ban" />
                  Terminate
                </div>
              </button>
            </div>
          ) : (
            ' '
          )}
          <CopyData info={shop} />
          {user.userType === 'ADMIN' &&
            shop.shopType === 'PLUS' && (
              <div className="l-flex-end u-margin-bottom">
                <div className="l-block-text-center">
                  <button
                    className="c-btn-medium c-btn-error"
                    onClick={() => {
                      handleShowDowngradeModal(shop);
                    }}
                    disabled={isNotDowngradable(shop)}
                  >
                    <div className="c-btn__inner">
                      <i className="c-btn__icon fa fa-thumbs-down" />
                      Downgrade
                    </div>
                  </button>
                  {!!isNotDowngradable(shop) && (
                    <>
                      <h3>
                        Ineligible. Check Shop Type, Users, Tools,
                        Subscription State, and Subscription ID
                      </h3>
                    </>
                  )}
                </div>
              </div>
            )}

          <Dialog
            isOpen={showConfirmInactivateModal}
            onDismiss={handleHideInactivateModal}
            className="c-modal"
            aria-label={'Confirm Downgrade Shop'}
          >
            <button
              className="c-btn-icon c-modal__close"
              onClick={handleHideInactivateModal}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fa fa-times" />
              </div>
            </button>
            <h1 className="c-modal__heading">Confirm Shop Inactivation</h1>

            <div className="c-modal__body">
              <div className='u-margin-bottom-large'>
                <span className='p'>
                  Are you sure you want to <span className='text-bold'>inactivate</span> this shop:{' '}
                </span>
                <pre>
                  <strong>{shop.shopName}</strong>
                </pre>
                <span className='p'>
                  This action cannot be undone. <span className='text-bold'>All tools and users will be removed, the shop's state will be set to INACTIVE, and its Braintree subscription will be canceled.</span>
                </span>
                <span className='p u-margin-bottom-large'>
                  {' '}If so, please confirm by typing the shop name below (case
                  sensitive).
                </span>
              </div>

              <div className="c-field u-margin-bottom-large">
                <label htmlFor="confirmShopName" className="c-field__label">
                  Confirm Shop Name
                </label>
                <input
                  autoFocus
                  id="confirmShopName"
                  name="confirmShopName"
                  maxLength={50}
                  type="text"
                  className="c-input"
                  placeholder={shop.shopName}
                  value={confirmShopName}
                  onChange={handleConfirmShopNameChange}
                />
              </div>

              <div className="l-flex-end">
                <button
                  className="c-btn-outline"
                  onClick={handleHideInactivateModal}
                >
                  Don't Inactivate
                </button>
                <LoaderButton
                  onClick={() => {
                    handleInactivateShop(shop.shopID);
                  }}
                  className="c-btn c-btn-error u-margin-left"
                  disabled={
                    !confirmShopName ||
                    !shop?.shopName ||
                    confirmShopName.trim() !== shop.shopName.trim()
                  }
                  isLoading={isLoading}
                  text="Inactivate Shop"
                  loadingText="Inactivating..."
                  icon={'fa-regular fa-power-off'}
                />
              </div>
            </div>
          </Dialog>

          <Dialog
            isOpen={showConfirmTerminateModal}
            onDismiss={handleHideTerminateModal}
            className="c-modal"
            aria-label={'Confirm Terminate Shop'}
          >
            <button
              className="c-btn-icon c-modal__close"
              onClick={handleHideTerminateModal}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fa fa-times" />
              </div>
            </button>
            <h1 className="c-modal__heading">Confirm Shop Termination</h1>

            <div className="c-modal__body">
              <p>Are you sure you want to terminate this shop? : </p>
              <pre>
                <strong>{shop.shopName}</strong>
              </pre>
              <p>
                If so, please confirm by typing the shop name below (case
                sensitive).
              </p>
              <div className="c-field">
                <label htmlFor="confirmShopName" className="c-field__label">
                  Confirm Shop Name
                </label>
                <input
                  autoFocus
                  id="confirmShopName"
                  name="confirmShopName"
                  maxLength={50}
                  type="text"
                  className="c-input"
                  placeholder={shop.shopName}
                  value={confirmShopName}
                  onChange={handleConfirmShopNameChange}
                />
              </div>
              <div className="l-flex-end">
                <button
                  className="c-btn-outline"
                  onClick={handleHideTerminateModal}
                >
                  Don't Terminate
                </button>
                <LoaderButton
                  onClick={() => {
                    handleTerminateShop(shop.shopID);
                  }}
                  className="c-btn c-btn-error u-margin-left"
                  disabled={
                    !confirmShopName ||
                    !shop?.shopName ||
                    confirmShopName.trim() !== shop.shopName.trim()
                  }
                  isLoading={isLoading}
                  text="Terminate Shop"
                  loadingText="Terminating..."
                  icon={'fa fa-ban'}
                />
              </div>
            </div>
          </Dialog>

          <Dialog
            isOpen={showConfirmDowngradeModal}
            onDismiss={handleHideDowngradeModal}
            className="c-modal"
            aria-label={'Confirm Downgrade Shop'}
          >
            <button
              className="c-btn-icon c-modal__close"
              onClick={handleHideDowngradeModal}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fa fa-times" />
              </div>
            </button>
            <h1 className="c-modal__heading">Confirm Shop Downgrade</h1>

            <div className="c-modal__body">
              <p>
                Are you sure you want to Downgrade this shop to STANDARD? :{' '}
              </p>
              <pre>
                <strong>{shop.shopName}</strong>
              </pre>
              {(shop.crmState === 'ACTIVE' ||
                shop.crmState === 'SUSPENDED' ||
                shop.crmState === 'CHARGEBACKRECEIVED') && (
                <p>
                  This shop currently has an active CRM plan. Downgrading to
                  the Standard plan{' '}
                  <strong>will remove access to all CRM features</strong>. Are
                  you sure you want to continue with the downgrade?
                </p>
              )}
              <p>
                If so, please confirm by typing the shop name below (case
                sensitive).
              </p>
              <div className="c-field">
                <label htmlFor="confirmShopName" className="c-field__label">
                  Confirm Shop Name
                </label>
                <input
                  autoFocus
                  id="confirmShopName"
                  name="confirmShopName"
                  maxLength={50}
                  type="text"
                  className="c-input"
                  placeholder={shop.shopName}
                  value={confirmShopName}
                  onChange={handleConfirmShopNameChange}
                />
              </div>

              <div className="l-flex-end">
                <button
                  className="c-btn-outline"
                  onClick={handleHideDowngradeModal}
                  disabled={isLoading}
                >
                  Don't Downgrade
                </button>
                <LoaderButton
                  onClick={() => {
                    handleDowngradeShop(shop.shopID);
                  }}
                  className="c-btn c-btn-error u-margin-left"
                  disabled={
                    isLoading ||
                    !confirmShopName ||
                    !shop?.shopName ||
                    confirmShopName.trim() !== shop.shopName.trim() ||
                    isNotDowngradable(shop)
                  }
                  isLoading={isLoading}
                  text="Downgrade Shop"
                  loadingText="Downgrading..."
                  icon={'fa fa-ban'}
                />
              </div>
            </div>
          </Dialog>

          <Dialog
            isOpen={showTaxExemptModal}
            onDismiss={handleHideTaxExemptModal}
            className="c-modal c-modal--tall"
            aria-label={'Mark Shop Tax Exempt'}
          >
            <button
              className="c-btn-icon c-modal__close"
              onClick={handleHideTaxExemptModal}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fa fa-times" />
              </div>
            </button>
            <h1 className="c-modal__heading">Mark Shop Tax Exempt</h1>

            <div className="c-modal__body">
              <p className="u-margin-top-none">
                <strong>{shop.shopName}</strong>
              </p>
              <div className="c-field">
                <label htmlFor="taxpayerIdNumber" className="c-field__label">
                  Taxpayer ID
                </label>
                <input
                  autoFocus
                  id="taxpayerIdNumber"
                  name="taxpayerIdNumber"
                  maxLength={50}
                  type="text"
                  className="c-input"
                  placeholder="Taxpayer ID Number"
                  value={taxpayerIdNumber}
                  onChange={(e) => setTaxpayerIdNumber(e.target.value)}
                />
              </div>
              <div className="c-field">
                <label htmlFor="taxCertEffectiveDate" className="c-field__label">
                  Tax Certificate Effective Date
                </label>
                <DateRangePicker
                  initialSettings={{
                    singleDatePicker: true,
                    showDropdowns: true,
                    startDate: new Date(),
                    minYear: 1960,
                    maxDate: new Date(),
                  }}
                  onCallback={(date) => {
                    setTaxCertEffectiveDate(date.format('YYYY-MM-DD'));
                  }}
                >
                  <input
                    type="text"
                    className="form-control col-4"
                    id="taxCertEffectiveDate"
                    name="taxCertEffectiveDate"
                  />
                </DateRangePicker>
              </div>
              <div className="c-field">
                <label htmlFor="exemptionReason" className="c-field__label">
                  Exemption Reason
                </label>
                {exemptionReasons.length !== 0 ? (
                  <Typeahead
                    id="exemptionReason"
                    labelKey="name"
                    options={exemptionReasons}
                    placeholder="Select an exemption reason..."
                    onChange={(selected) => {
                      setSelectedReason(selected[0]);
                      setReasonSelections(selected);
                    }}
                    filterBy={['name']}
                    multiple={false}
                    selected={reasonSelections}
                  />
                ) : (
                  <i
                    className="fal fa-spinner-third spinning c-btn-icon"
                    title={'Loading...'}
                  />
                )}
              </div>
              <div className="c-field">
                <label htmlFor="taxCertificate" className="c-field__label">
                  Tax Certificate
                </label>
                {taxCert && taxCertInfo()}

                {taxCert ? (
                  <div className="c-upload-preview scrolling u-margin-top-large">
                    <Document
                      file={taxCert || null}
                      loading={TextOnly('loading')}
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      {Array.from(new Array(numPages), (_, i) => (
                        <>
                          <div className="u-margin-top-small"></div>
                          <Page key={`page_${i + 1}`} pageNumber={i + 1} scale={1} />
                        </>
                      ))}
                    </Document>
                  </div>
                ) : (
                  <FileUpload
                    name="taxCertificate"
                    description="Drag & drop the shop's tax exemption certificate"
                    handleOnDrop={(file) => attachTaxCert(file)}
                    fullWidth
                  />
                )}
                {fileError && (
                  <span className="u-font-size-small u-text-error">
                    {fileError}
                  </span>
                )}
                {!taxCert && !fileError && (
                  <span className="u-font-size-small u-text-info">
                    PDF file format; {displayMaxFilesize()} max
                  </span>
                )}
              </div>
            </div>
            <div className="l-flex-end">
              <button className="c-btn-outline" onClick={handleHideTaxExemptModal}>
                Cancel
              </button>
              <LoaderButton
                onClick={() => {
                  const reason = selectedReason;
                  const exemptionReason = reason.name;

                  handleMarkShopTaxExempt(
                    shop.shopID,
                    taxpayerIdNumber,
                    taxCert,
                    taxCertEffectiveDate,
                    exemptionReason,
                    exposureZone,
                    UX.MAX_AVALARA_RETRY_ATTEMPTS
                  );
                }}
                className="c-btn u-margin-left"
                isLoading={isLoading}
                text="Mark Tax Exempt"
                loadingText="Marking Tax Exempt..."
                disabled={isMissingInfo()}
              />
            </div>
          </Dialog>

          {user.userType === 'ADMIN' && !inactive && (
            <button
              className="c-btn-medium c-btn-error u-margin-bottom"
              onClick={handleShowInactivateModal}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fa-regular fa-power-off" />
                Inactivate
              </div>
            </button>
          )}
        </div>
      )}

      {/* Deskop */}
      {windowWidth > 1220 && (
        <>
          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">Basic Shop Info</h2>
            <div className="c-card__description">
              <table className="card-table">
                <colgroup>
                  <col width="15%" />
                  <col />
                  <col width="15%" />
                  <col />
                </colgroup>
                <tbody>
                  <tr className="card-tr">
                    {isNotExternal && !insideGetUserSummary &&
                      <>
                        <th className="card-th">Shop ID: </th>
                        <td className="card-td">
                          <>
                            {queryType !== 'SHOP ID' ? (
                              <button
                                className="c-btn-link-thin u-no-wrap u-font-mono width-100 display-flex"
                                onClick={() => handleQueryShopID()}
                              >
                                <span className="u-font-mono">
                                  {shop.shopID}
                                </span>
                              </button>
                            ) : (
                              <span className="u-font-mono">
                                {shop.shopID}
                              </span>
                            )}
                          </>

                          <button
                            className="c-btn-icon"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(shop.shopID as string)
                                .then(function () {
                                  toast.success('Copied!', {
                                    containerId: 'standard',
                                  });
                                });
                            }}
                          >
                            <div className="c-btn__inner">
                              <span
                                className="c-btn__icon fa fa-clipboard"
                                title="Copy"
                              />
                            </div>
                          </button>
                        </td>
                      </>
                    }
                    {insideGetUserSummary &&
                      <>
                        <th className="card-th">{isNotExternal ? 'Shop ID' : 'Shop Name'}:</th>
                        <td className="card-td u-font-mono">
                          {
                            <CopyLink
                              content={isNotExternal ? shop.shopID : shop.shopName}
                              redirect={'/shopFunctions/getShopSummary'}
                              type={'SHOP ID'}
                              action={'search'}
                              urlParams={`?query=${shop.shopID}&queryType=SHOP+ID`} // External Support Portal Phase 1. Adjust in future phase if wanted.
                            />
                          }
                        </td>
                      </>
                    }
                    <th className="card-th">Created On: </th>
                    <td className="card-td">
                      {formatTimezone(
                        shop.createdOn,
                        user.userTimezone
                      )}
                    </td>
                  </tr>
                  <tr className="card-tr">
                    <th className="card-th">Shop Type: </th>
                    <td className="card-td">
                      <Chip
                        fontWeight="bold"
                        backgroundColor={(() => {
                          switch (shop.shopType) {
                            case 'STANDARD':
                              return '#556066';
                            case 'PLUS':
                              return '#0A4766';
                            case 'ENTERPRISE':
                              return '#C2000C';
                            default:
                              return '#0088CC';
                          }
                        })()}
                        color="#fff"
                        border="none"
                      >
                        {shop.shopType}
                      </Chip>{' '}
                      {shop.upgradeNeeded ? (
                        <span className="u-text-warning">
                          (Marked for Upgrade to PLUS)
                        </span>
                      ) : shop.downgradeToStandardPending ? (
                        <span className="u-text-warning">
                          (Pending Downgrade to STANDARD)
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                    <th className="card-th">Max Users: </th>
                    <td className="card-td">
                      <div className="l-flex-align-center">
                        {shop.shopMaxUsers}
                        {'  '}
                        {user.userType === 'ADMIN'
                          && (shop.shopMaxUsers ?? 0) >
                            config[`${shop.shopType?.toLowerCase()}BaseUsers`]
                          && (shopUsers?.length ?? 0) < shop.shopMaxUsers
                          && shop.shopToolsCount < shop.shopMaxUsers && (
                            <button
                              className="c-btn-icon"
                              onClick={() => handleShowRemoveUserModal()}
                            >
                              <div className="c-btn__inner">
                                <i className="c-btn__icon fal fa-edit" />
                              </div>
                            </button>
                          )
                        }
                      </div>
                    </td>
                  </tr>
                  <tr className="card-tr">
                    {shop.shopTools ? (
                      <>
                        <th className="card-th">Number of Tools: </th>
                        <td className="card-td">
                          <div className="l-flex-align-center">
                            {shop.shopToolsCount}{' '}
                            {shop.shopToolsCount > 0 && (
                              <button
                                className="c-btn-icon"
                                onClick={() =>
                                  handleShowTableModal('tools', shop)
                                }
                              >
                                <div className="c-btn__inner">
                                  <i className="c-btn__icon fa fa-chevron-right" />
                                </div>
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    ) : null}

                    {shopUsers ? (
                      <>
                        <th className="card-th">Number of Users: </th>
                        <td className="card-td">
                          <div className="l-flex-align-center">
                            {shopUsers?.length ?? 0}{' '}
                            {(shopUsers?.length ?? 0) > 0 && (
                              <button
                                className="c-btn-icon"
                                onClick={() =>
                                  handleShowTableModal('users', shop)
                                }
                              >
                                <div className="c-btn__inner">
                                  <i className="c-btn__icon fa fa-chevron-right" />
                                </div>
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    ) : null}
                  </tr>
                  <tr className="card-tr">
                    <th
                      className="card-th"
                      style={{ verticalAlign: 'text-top' }}
                    >
                      Region:{' '}
                    </th>
                    <td className="card-td">
                      <span>{oemInfo[shop.regionCode]}</span>
                    </td>

                    <th className="card-th">Max Tech Certs: </th>
                    <td className="card-td">
                      <div className="l-flex-align-center">
                        {maxTechCerts ?? 0}
                        {'  '}
                        {user.userType === 'ADMIN' &&
                          (maxTechCerts ?? 0) >
                            config[
                              `${shop.shopType?.toLowerCase()}BaseTechCerts`
                            ] && (
                            <button
                              className="c-btn-icon"
                              onClick={() => handleShowRemoveTechCertModal()}
                            >
                              <div className="c-btn__inner">
                                <i className="c-btn__icon fal fa-edit" />
                              </div>
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                  <tr className="card-tr">
                    <th
                      className="card-th"
                      style={{ verticalAlign: 'text-top' }}
                    >
                      OEMs:{' '}
                    </th>
                    <td className="card-td align-top">
                      <ul className="u-margin-top-none u-padding-left-none u-list-unstyled">
                    {
                      shop.oemIDs?.map((oemID: string) => (
                        <li key={oemID} className="l-flex-row-center">
                        { (user.userType === 'ADMIN' && shop.oemIDs.length !== 1) &&
                          <button
                            className="c-btn-icon--rm-oem u-padding-none u-margin-right-small"
                            onClick={() => handleShowRemoveOEMModal(oemID)}
                          >
                            <span className="fal fa-circle-xmark" title="Remove OEM" />
                          </button>
                        }
                          {oemInfo[oemID]}
                        { shop.removedOemIDs?.includes(oemID) &&
                          <span className="u-text-warning u-margin-left-small">(Pending Removal)</span>
                        }
                        </li>
                      ))
                    }
                      </ul>
                    </td>

                    <th className="card-th">
                      Number of Used Tech Certs:{' '}
                    </th>
                    <td className="card-td">
                      <div className="l-flex-align-center">
                        {shop.numUsedTechCerts ?? 0}
                        {(shop.numUsedTechCerts ?? 0) >
                          (maxTechCerts ?? 0) && (
                          <div className="u-text-error u-font-size-small u-padding-left">
                            *Some Tech Certs have been removed from shop
                            subscription
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Shop State and Address Info */}
          <div className="l-flex-between">
            <div>
              <div className="c-card u-margin-right-xlarge u-margin-bottom">
                <h2 className="c-card__title">Shop States</h2>
                <div className="c-card__description">
                  <table className="card-table">
                    <tbody>{shopStateCard}</tbody>
                  </table>
                  {shop.hasOwnProperty('crmState') && (
                    <table className="card-table u-padding-top-xlarge">
                      <tbody>{crmStateCard}</tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="c-card u-margin-right-xlarge u-margin-bottom">
                <h2 className="c-card__title">Address Info</h2>
                <div className="c-card__description">
                  <table className="card-table">
                    <tbody>{addressInfoCard}</tbody>
                  </table>
                </div>
              </div>

              <div className="c-card u-margin-right-xlarge u-margin-bottom">
                <h2 className="c-card__title">CRM Info</h2>
                <div className="c-card__description">
                  <table className="card-table">
                    <tbody>
                      <tr className="card-tr">
                        {shop.crmShopOrders ? (
                          <>
                            <th className="card-th">
                              Number of Orders:{' '}
                            </th>
                            <td className="card-td">
                              <div className="l-flex-align-center">
                                <span className="table-data-number">
                                  {shop.crmShopOrders?.length}{' '}
                                </span>
                                {(shop.crmShopOrders?.length || 0) >
                                  0 && (
                                  <button
                                    className="c-btn-icon"
                                    onClick={() =>
                                      handleShowTableModal(
                                        'shopOrders',
                                        shop
                                      )
                                    }
                                  >
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fa fa-chevron-right" />
                                    </div>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      <tr className="card-tr">
                        {shop.crmShopServices ? (
                          <>
                            <th className="card-th">
                              Number of Services:{' '}
                            </th>
                            <td className="card-td">
                              <div className="l-flex-align-center">
                                <span className="table-data-number">
                                  {shop.crmShopServices?.length}{' '}
                                </span>
                                {(shop.crmShopServices?.length || 0) >
                                  0 && (
                                  <button
                                    className="c-btn-icon"
                                    onClick={() =>
                                      handleShowTableModal(
                                        'shopServices',
                                        shop
                                      )
                                    }
                                  >
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fa fa-chevron-right" />
                                    </div>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      <tr className="card-tr">
                        {shop.crmShopParts ? (
                          <>
                            <th className="card-th">
                              Number of Parts:{' '}
                            </th>
                            <td className="card-td">
                              <div className="l-flex-align-center">
                                <span className="table-data-number">
                                  {shop.crmShopParts?.length}{' '}
                                </span>
                                {(shop.crmShopParts?.length || 0) >
                                  0 && (
                                  <button
                                    className="c-btn-icon"
                                    onClick={() =>
                                      handleShowTableModal(
                                        'shopParts',
                                        shop
                                      )
                                    }
                                  >
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fa fa-chevron-right" />
                                    </div>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      <tr className="card-tr">
                        {shop.crmShopCustomers ? (
                          <>
                            <th className="card-th">
                              Number of Customers:{' '}
                            </th>
                            <td className="card-td">
                              <div className="l-flex-align-center">
                                <span className="table-data-number">
                                  {shop.crmShopCustomers?.length}{' '}
                                </span>

                                {(shop.crmShopCustomers?.length || 0) >
                                  0 && (
                                  <button
                                    className="c-btn-icon"
                                    onClick={() => {
                                      handleShowTableModal(
                                        'shopCustomers',
                                        shop
                                      );
                                    }}
                                  >
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fa fa-chevron-right" />
                                    </div>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      <tr className="card-tr">
                        {shop.crmShopEvents ? (
                          <>
                            <th className="card-th">
                              Number of Events:{' '}
                            </th>
                            <td className="card-td">
                              <div className="l-flex-align-center">
                                <span className="table-data-number">
                                  {shop.crmShopEvents?.length}{' '}
                                </span>
                                {(shop.crmShopEvents?.length || 0) >
                                  0 && (
                                  <button
                                    className="c-btn-icon"
                                    onClick={() =>
                                      handleShowTableModal(
                                        'shopEvents',
                                        shop
                                      )
                                    }
                                  >
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fa fa-chevron-right" />
                                    </div>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Subscription Info and Shop Owner Info*/}
            {shop.shopPaymentInfo ? (
              <div>
                <div className="c-card u-margin-left-xlarge u-margin-bottom">
                  <h2 className="c-card__title">
                    Subscription and Payment Info
                  </h2>
                  <div className="c-card__description">
                    <table className="card-table">
                      <tbody>{paymentInfoCard}</tbody>
                    </table>
                    {shop.hasOwnProperty('crmState') && (
                      <table className="card-table u-padding-top-xlarge">
                        <tbody>{crmPaymentInfoCard}</tbody>
                      </table>
                    )}
                    <table className="card-table u-margin-top-xlarge">
                      <tbody>
                        <tr className={`card-tr ${shop.shopPaymentInfo?.taxAmount && shop.shopPaymentInfo?.taxAmount > 0 && isAdmin ? 'u-padding-bottom-none' : ''}`}>
                          <th className={`card-th ${shop.shopPaymentInfo?.taxAmount && shop.shopPaymentInfo?.taxAmount > 0 && isAdmin ? 'u-padding-bottom-none' : ''}`}>Tax Exempt:</th>
                          <td className={`card-td ${shop.shopPaymentInfo?.taxAmount && shop.shopPaymentInfo?.taxAmount > 0 && isAdmin ? 'u-padding-bottom-none' : ''} l-flex-align-center`}>
                            <span>{taxExemptStatus}</span>
                          {
                            showTaxExemptButton &&
                            <button
                              className="c-btn-medium c-btn-error"
                              onClick={() => {
                                handleShowTaxExemptModal(shop);
                              }}
                              disabled={disableTaxExemptButton}
                            >
                              <div className="c-btn__inner">Mark Tax Exempt</div>
                            </button>
                          }
                          </td>
                        </tr>
                      {
                        isAdmin && disableTaxExemptButton &&
                        <tr className="card-tr">
                          <th className="u-padding-none"></th>
                          <td className="u-padding-none u-line-height-100">
                            <em className="u-text-error u-font-size-small">
                              An address is required to mark a shop tax exempt. Please ask its owner to add one before proceeding.
                            </em>
                          </td>
                        </tr>
                      }
                        {shop?.taxExempt !== true ?
                          <tr className="card-tr">
                            <th className="card-th">Tax Rate:</th>
                            <td className="card-td l-flex-align-center">
                              {shop.shopPaymentInfo?.taxAmount && 
                                shop.shopPaymentInfo?.subTotal &&
                                shop.shopPaymentInfo?.subTotal > 0
                                ?
                                <>
                                  {
                                    parseFloat(((shop.shopPaymentInfo?.taxAmount / shop.shopPaymentInfo?.subTotal) * 100).toFixed(2))
                                  }%
                                </>
                                : 
                                <span>
                                  0%
                                </span>
                              }
                            </td>
                          </tr>
                          : null
                        }
                        {transactionsCard}
                      </tbody>
                    </table>
                    <div className="u-margin-top-xlarge u-margin-bottom-large">
                      <Cards
                        cvc=""
                        expiry={
                          shop.shopPaymentInfo?.expirationDate || '00/00'
                        }
                        name=" "
                        number={
                          getCreditCardString(
                            shop.shopPaymentInfo?.cardType
                          ) + (shop.shopPaymentInfo?.last4 || '••••')
                        }
                        issuer={getCardIssuer(
                          shop.shopPaymentInfo?.cardType || 'None'
                        )}
                        preview={true}
                      />
                    </div>
                  </div>
                </div>

                {shop.shopOwner ? (
                  <div className="c-card u-margin-left-xlarge">
                    <h2 className="c-card__title">Shop Owner</h2>
                    <div className="c-card__description">
                      <table className="card-table">
                        <colgroup>
                          <col />
                          <col />
                        </colgroup>
                        <tbody>{shopOwnerInfoCard}</tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            ) : (
              <div>
                <div className="c-card u-margin-left-xlarge u-margin-bottom">
                  <h2 className="c-card__title">
                    Subscription and Payment Info
                  </h2>
                  <h2>There is no Subscription or Payment Info</h2>
                  <div className="u-margin-top-xlarge u-margin-bottom-large"></div>
                </div>
              </div>
            )}
          </div>

          <hr />
          {
            <ShopLogStandalone
              shop={shop}
              handleShowAlert={handleShowAlert}
              onChange={(shopLog) => handleLogData(shopLog)}
            />
          }
        </>
      )}

      {/* Mobile */}
      {windowWidth < 1220 && (
        <>
          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">Basic Shop Info</h2>
            <div className="c-card__description">
              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">Shop ID</label>
                <div className="mobile-display-value u-font-mono">
                  {[
                    'SHOP NAME',
                    'CREDIT CARD LAST 4',
                    'TRANSACTION LAST 4',
                    'ADDRESS',
                    'PHONE NUMBER',
                  ].indexOf(queryType ?? '') !== -1 &&
                  !isSingleShopResult ? (
                    <button
                      className="c-btn-link-thin"
                      onClick={() => handleQueryShopID()}
                    >
                      {shop.shopID.toLowerCase()}
                    </button>
                  ) : (
                    <span>{shop.shopID}</span>
                  )}
                </div>
              </div>

              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">Max Users</label>
                <div className="mobile-display-value">
                  {shop.shopMaxUsers}
                </div>
              </div>

              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">Shop Type</label>
                <div className="mobile-display-value">
                  <Chip
                    fontWeight="bold"
                    backgroundColor={(() => {
                      switch (shop.shopType) {
                        case 'STANDARD':
                          return '#556066';
                        case 'PLUS':
                          return '#0A4766';
                        case 'ENTERPRISE':
                          return '#C2000C';
                        default:
                          return '#0088CC';
                      }
                    })()}
                    color="#fff"
                    border="none"
                  >
                    {shop.shopType}
                  </Chip>
                </div>
              </div>

              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">Region</label>
                <div className="mobile-display-value">
                  <div>
                    <span>{oemInfo[shop.regionCode]}</span>
                    <br />
                  </div>
                </div>
              </div>

              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">OEMs</label>
                <div className="mobile-display-value">
                  {shop.oemIDs
                    ?.map((oemID: string) => oemInfo[oemID])
                    .join(', ')}
                </div>
              </div>

              <div className="u-margin-bottom-large">
                <label className="mobile-display-label">Created On</label>
                <div className="mobile-display-value">
                  {formatTimezone(
                    shop.createdOn,
                    user.userTimezone
                  )}
                </div>
              </div>

              {isNotExternal && shop.shopTools && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Tools
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.shopToolsCount > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.shopToolsCount}{' '}
                    </div>
                    {shop.shopToolsCount > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('tools', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shopUsers && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Users
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        (shopUsers?.length ?? 0) > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {(shopUsers?.length ?? 0)}{' '}
                    </div>
                    {(shopUsers?.length ?? 0) > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('users', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shop State and Address Info */}
          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">Shop States</h2>
            <div className="c-card__description">{shopStateCard}</div>
          </div>

          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">Address Info</h2>
            <div className="c-card__description">{addressInfoCard}</div>
          </div>
          {/* Subscription Info and Shop Owner Info*/}
          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">
              Subscription and Payment Info
            </h2>
            <div className="c-card__description">
              {paymentInfoCard}
              <div className="l-flex-center u-margin-top-xlarge u-margin-bottom-large">
                <Cards
                  cvc=""
                  expiry={shop.shopPaymentInfo?.expirationDate || '00/00'}
                  name=" "
                  number={
                    getCreditCardString(shop.shopPaymentInfo.cardType) +
                    (shop.shopPaymentInfo?.last4 || '••••')
                  }
                  issuer={getCardIssuer(
                    shop.shopPaymentInfo?.cardType || 'None'
                  )}
                  preview={true}
                />
              </div>
            </div>
          </div>

          {/* Mobile CRM Info */}
          <div className="c-card u-margin-bottom-large">
            <h2 className="c-card__title">CRM Info</h2>
            <div className="c-card__description">
              {shop.crmShopOrders && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Orders
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.crmShopOrders?.length > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.crmShopOrders?.length}{' '}
                    </div>
                    {shop.crmShopOrders?.length > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('shopOrders', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shop.crmShopServices && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Services
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.crmShopServices?.length > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.crmShopServices?.length}{' '}
                    </div>
                    {shop.crmShopServices?.length > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('shopServices', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shop.crmShopParts && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Parts
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.crmShopParts?.length > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.crmShopParts?.length}{' '}
                    </div>
                    {shop.crmShopParts?.length > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('shopParts', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shop.crmShopCustomers && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Customers
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.crmShopCustomers?.length > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.crmShopCustomers?.length}{' '}
                    </div>
                    {shop.crmShopCustomers?.length > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('shopCustomers', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shop.crmShopEvents && (
                <div className="u-margin-bottom-large">
                  <label className="mobile-display-label">
                    Number of Events
                  </label>
                  <div className="l-flex-align-stretch">
                    <div
                      className={
                        shop.crmShopEvents?.length > 0
                          ? 'mobile-display-value-with-button'
                          : 'mobile-display-value'
                      }
                    >
                      {shop.crmShopCustomers?.length}{' '}
                    </div>
                    {shop.crmShopEvents?.length > 0 && (
                      <div
                        className="mobile-display-value-button"
                        onClick={() =>
                          handleShowTableModal('shopEvents', shop)
                        }
                      >
                        <i className="c-btn__icon fa fa-chevron-right u-padding-left" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {shop.shopOwner ? (
            <div className="c-card u-margin-bottom-large">
              <h2 className="c-card__title">Shop Owner</h2>
              <div className="c-card__description">
                <table className="card-table">
                  <colgroup>
                    <col />
                    <col />
                  </colgroup>
                  <tbody>{shopOwnerInfoCard}</tbody>
                </table>
              </div>
            </div>
          ) : (
            <div></div>
          )}
          <hr />
          {
            <ShopLogStandalone
              shop={shop}
              handleShowAlert={handleShowAlert}
              onChange={(shopLog) => handleLogData(shopLog)}
            />
          }
        </>
      )}

      <Dialog
        isOpen={showRemoveTechCertModal}
        onDismiss={handleHideRemoveTechCertModal}
        className="c-modal"
        aria-label={'Remove Shop Tech Certs'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={handleHideRemoveTechCertModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Remove Shop Tech Certs</h1>

        <div className="c-modal__body">
          <table className="u-width-100-percent">
            <colgroup>
              <col width="60%" />
              <col width="5%" />
              <col width="35%" />
            </colgroup>
            <tbody>
              <tr className="card-tr">
                <th className="card-th">Shop's Total Tech Certs: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {maxTechCerts}
                  </div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">Tech Certs Purchased: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {maxTechCerts - baseTechCerts}
                  </div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">
                  Purchased Tech Certs that are unused:{' '}
                </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {unusedPurchasedTechCerts}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <p className="l-flex-center l-flex-row u-align-center u-margin-top-xlarge u-margin-bottom-xlarge">
            Remove
            <Counter
              className="u-margin-left u-margin-right"
              value={numTechCertsToRemove}
              min={1}
              max={maxTechCerts - baseTechCerts}
              handleChange={(val: number) => {
                setNumTechCertsToRemove(val);
              }}
            />
            Tech Cert(s) from shop {shop.shopName}.
          </p>

          <p>
            Are you sure you want to Remove{' '}
            <strong>{numTechCertsToRemove}</strong> Tech Cert(s)?
          </p>
          <pre>
            <strong>{shop.shopName}</strong>
          </pre>
          <p>
            If so, please confirm by typing the shop name below (case
            sensitive).
          </p>
          <div className="c-field">
            <label htmlFor="confirmShopName" className="c-field__label">
              Confirm Shop Name
            </label>
            <input
              autoFocus
              id="confirmShopName"
              name="confirmShopName"
              maxLength={50}
              type="text"
              className="c-input"
              placeholder={shop.shopName}
              value={confirmShopName}
              onChange={handleConfirmShopNameChange}
            />
          </div>

          <div className="l-flex-end">
            <button
              className="c-btn-outline"
              onClick={handleHideRemoveTechCertModal}
              disabled={isLoading}
            >
              Don't Remove Tech Certs
            </button>
            <LoaderButton
              onClick={() => {
                handleRemoveTechCerts(shop.shopID, numTechCertsToRemove);
              }}
              className="c-btn c-btn-error u-margin-left"
              disabled={
                isLoading ||
                !confirmShopName ||
                !shop?.shopName ||
                confirmShopName.trim() !== shop.shopName.trim()
              }
              isLoading={isLoading}
              text={`Remove ${numTechCertsToRemove} Tech Cert${
                numTechCertsToRemove === 1 ? '' : 's'
              }`}
              loadingText="Removing..."
              icon={'fa fa-ban'}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showTechCertRefundModal}
        onDismiss={handleHideTechCertRefundModal}
        className="c-modal"
        aria-label={'Refund Shop Tech Certs'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={handleHideTechCertRefundModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Refund Shop Tech Certs</h1>

        <div className="c-modal__body">
          <p>
            <strong>{numTechCertsToRemove}</strong> Tech Cert(s) have been
            successfully removed from shop {shop.shopName}.
          </p>
          {techCertsEligibleForRefund > 0 ? (
            <>
              <p>
                Of all Tech Certs removed,{' '}
                <strong>{techCertsEligibleForRefund}</strong> Tech Cert(s) were
                never used and are eligible for a refund.
              </p>
              <p>
                No Tech Certs have been refunded. To refund manually, click the
                button below to redirect to the shop transactions table.
              </p>
              <LoaderButton
                onClick={() => {
                  handleHideTechCertRefundModal();
                  handleShowTableModal('transactions', shop);
                }}
                className="c-btn"
                isLoading={isLoading}
                text="Go to Shop Transactions"
                loadingText="Redirecting to Shop Transactions..."
              />
            </>
          ) : (
            <p>
              All removed Tech Certs have been used and therefore are not
              eligible for a refund.
            </p>
          )}
        </div>
      </Dialog>

      <Dialog
        isOpen={showConfirmRemoveOEMModal}
        onDismiss={handleHideRemoveOEMModal}
        className="c-modal"
        aria-label={'Confirm Remove OEM and Refund'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={handleHideRemoveOEMModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Confirm Remove OEM and Refund</h1>

        <div className="c-modal__body">
          <p>
            Are you sure you want to Remove{' '}
            <strong>{oemInfo[oemToRemove]}</strong> and Refund? :{' '}
          </p>
          <pre>
            <strong>{shop.shopName}</strong>
          </pre>
          <p>
            If so, please confirm by typing the shop name below (case
            sensitive).
          </p>
          <div className="c-field">
            <label htmlFor="confirmShopName" className="c-field__label">
              Confirm Shop Name
            </label>
            <input
              autoFocus
              id="confirmShopName"
              name="confirmShopName"
              maxLength={50}
              type="text"
              className="c-input"
              placeholder={shop.shopName}
              value={confirmShopName}
              onChange={handleConfirmShopNameChange}
            />
          </div>

          <div className="l-flex-end">
            <button
              className="c-btn-outline"
              onClick={handleHideRemoveOEMModal}
              disabled={isLoading}
            >
              Don't Remove OEM
            </button>
            <LoaderButton
              onClick={() => {
                handleRemoveOEM(shop.shopID, oemToRemove);
              }}
              className="c-btn c-btn-error u-margin-left"
              disabled={
                isLoading ||
                !confirmShopName ||
                !shop?.shopName ||
                confirmShopName.trim() !== shop.shopName.trim()
              }
              isLoading={isLoading}
              text="Remove OEM"
              loadingText="Removing..."
              icon={'fa fa-ban'}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showRemoveUserModal}
        onDismiss={handleHideRemoveUserModal}
        className="c-modal"
        aria-label={'Remove Shop Users'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={handleHideRemoveUserModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Remove Shop Users</h1>

        <div className="c-modal__body">
          <table className="u-width-100-percent">
            <colgroup>
              <col width="60%" />
              <col width="5%" />
              <col width="35%" />
            </colgroup>
            <tbody>
              <tr className="card-tr">
                <th className="card-th">Shop's User/Tool Capacity: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">{maxUsersTools}</div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">Shop's Used Users: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {(shopUsers?.length ?? 0)}
                  </div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">Shop's Used Tools: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {shop.shopToolsCount}
                  </div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">Users/Tools Purchased: </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {purchasedUsersTools}
                  </div>
                </td>
              </tr>
              <tr className="card-tr">
                <th className="card-th">
                  Purchased Users/Tools that are unused:{' '}
                </th>
                <td className="card-td l-flex-end">
                  <div className="l-flex-align-center">
                    {unusedPurchasedUsersTools}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <p className="l-flex-center l-flex-row u-align-center u-margin-top-xlarge u-margin-bottom-xlarge">
            Remove
            <Counter
              className="u-margin-left u-margin-right"
              value={numUsersToRemove}
              min={1}
              max={purchasedUsersTools ? unusedPurchasedUsersTools : 0}
              handleChange={(val: number) => {
                setNumUsersToRemove(val);
              }}
            />
            User/Tool(s) from shop {shop.shopName}.
          </p>

          <p>
            Are you sure you want to Remove{' '}
            <strong>{numUsersToRemove}</strong> User/Tool(s)?
          </p>
          <pre>
            <strong>{shop.shopName}</strong>
          </pre>
          <p>
            If so, please confirm by typing the shop name below (case
            sensitive).
          </p>
          <div className="c-field">
            <label htmlFor="confirmShopName" className="c-field__label">
              Confirm Shop Name
            </label>
            <input
              autoFocus
              id="confirmShopName"
              name="confirmShopName"
              maxLength={50}
              type="text"
              className="c-input"
              placeholder={shop.shopName}
              value={confirmShopName}
              onChange={handleConfirmShopNameChange}
            />
          </div>

          <div className="l-flex-end">
            <button
              className="c-btn-outline"
              onClick={handleHideRemoveUserModal}
            >
              Don't Remove Users
            </button>
            <LoaderButton
              onClick={() => {
                handleRemoveUsers(shop.shopID, numUsersToRemove);
              }}
              className="c-btn c-btn-error u-margin-left"
              disabled={
                !confirmShopName ||
                !shop?.shopName ||
                confirmShopName.trim() !== shop.shopName.trim()
              }
              isLoading={isLoading}
              text={`Remove ${numUsersToRemove} User${
                numUsersToRemove === 1 ? '' : 's'
              }`}
              loadingText="Removing..."
              icon={'fa fa-ban'}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showUserRefundModal}
        onDismiss={handleHideUserRefundModal}
        className="c-modal"
        aria-label={'Refund Shop Users'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={handleHideUserRefundModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Refund Shop Users/Tools</h1>

        <div className="c-modal__body">
          <p>
            <strong>{numUsersToRemove}</strong> User/Tool(s) have been
            successfully removed from shop {shop.shopName}.
          </p>
          <p>
            No User/Tools have been refunded. To refund manually, click the
            button below to redirect to the shop transactions table.
          </p>
          <LoaderButton
            onClick={() => {
              handleHideUserRefundModal();
              handleShowTableModal('transactions', shop);
            }}
            className="c-btn"
            isLoading={isLoading}
            text="Go to Shop Transactions"
            loadingText="Redirecting to Shop Transactions..."
          />
        </div>
      </Dialog>

      {/* Tools, Users, and Transactions Tables */}
      <DisplayDialog
        title={`${shop.shopName} Tools` || 'Shop Tools'}
        isOpen={showToolTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_TOOLS-table"
          data={shop.shopTools as any[]}
          filterKeys={[
            'toolModel',
            'toolManufacturer',
            'toolSerial',
            'addedBy',
            'toolID',
          ]}
          columnDefs={[
            {
              Header: 'Manufacturer',
              accessor: 'toolManufacturer',
              Cell: (row: any) => {
                const regionCode = shop.regionCode;
                const manufacturer = row.original?.regions?.find(
                  (item: any) => {
                    return (
                      item?.regionCode === regionCode && !!item?.displayName
                    );
                  }
                );
                if (!regionCode || !manufacturer)
                  return <span>{row.original.toolManufacturer}</span>;
                else return <span>{manufacturer.displayName}</span>;
              },
            },
            ...shopToolColumns.filter(
              (item) => item.Header !== 'Manufacturer'
            ),

            ...(shop.shopType !== 'STANDARD'
              ? [
                  {
                    Header: 'Tool Name',
                    accessor: 'toolName',
                  },
                  {
                    Header: 'Tool Notes',
                    accessor: 'toolNotes',
                    Cell: (row: any) => {
                      return (
                        <ul>
                          {row.original.toolNotes?.map(
                            (note: any, idx: any) => (
                              <li key={idx}>{note}</li>
                            )
                          )}
                        </ul>
                      );
                    },
                  },
                ]
              : []),
            isNotExternal && {
              Header: 'Tool Id',
              accessor: 'toolID',
              Cell: (row: any) => {
                return <span className="tofw-ell">{row.original.toolID}</span>;
              },
            },
            isNotExternal && {
              Header: 'Added By',
              accessor: 'addedBy',
              Cell: (row: any) => {
                return loadingAddedBy === row.original.toolID ? (
                  <button className="c-btn-icon">
                    <div className="c-btn__inner">
                      <i className="c-btn__icon fal fa-spinner-third spinning" />
                    </div>
                  </button>
                ) : (
                  <button
                    className="c-btn-link-thin-chunk u-font-mono"
                    onClick={async () => {
                      setLoadingAddedBy(row.original.toolID);
                      let queriedUser = await getUserSummary(
                        'USERID',
                        row.original.addedBy
                      );
                      let userInfo = createUserInfoCard(
                        queriedUser,
                        windowWidth,
                        user.userType
                      );
                      let modalBody = (
                        <div className="c-card">
                          <h2 className="c-card__title">{`${queriedUser.firstName} ${queriedUser.lastName}`}</h2>
                          <div className="c-card__description">
                            <table className="card-table">
                              <colgroup>
                                <col className="card-table-col-labels" />
                                <col />
                              </colgroup>
                              <tbody>{userInfo}</tbody>
                            </table>
                          </div>
                          <Led status={queriedUser.userState as any} />
                        </div>
                      );
                      handleShowAlert(`User Summary`, modalBody);
                      setLoadingAddedBy('');
                    }}
                  >
                    {row.original.addedBy}
                  </button>
                );
              },
            },
            isAdvancedRole && {
              Header: 'Actions',
              accessor: 'toolModel',
              minWidth: 80,
              maxWidth: 80,
              Cell: (row: any) => {
                return (
                  <GenericPopover
                    anchorElement={
                      <button className="c-btn-icon">
                        <div className="c-btn__inner">
                          <span
                            className="c-btn__icon fal fa-trash-alt"
                            title="Remove Tool"
                          />
                        </div>
                      </button>
                    }
                    body={
                      <strong>
                        Are you sure you want to remove this tool?
                      </strong>
                    }
                    primaryBtnElement={
                      <LoaderButton
                        className="c-btn-medium c-btn-error"
                        disabled={false}
                        text="Yes, remove it"
                        loadingText="Removing..."
                        icon={'fal fa-trash-alt'}
                      />
                    }
                    secondaryBtnElement={
                      <button className="c-btn-medium c-btn-outline">
                        <div className="c-btn__inner">
                          <i className="c-btn__icon fal fa-times" />
                          No, not yet
                        </div>
                      </button>
                    }
                    primaryAction={async () => {
                      return handleRemoveTool(
                        shop.shopID,
                        row.original.toolID
                      );
                    }}
                    color="#fff"
                    position="top"
                    align="end"
                    arrowSize={5}
                    closeOnClick={false}
                  />
                );
              },
            },
          ]}
          defaultSorted={[
            {
              id: 'toolSerial',
              desc: false,
            },
          ]}
          savedColumnsId="toolsTableColumns"
          subComponentRenderer={
            isNotExternal ? LogSubcomponent : ExternalLogSubcomponent
          }
        />
      </DisplayDialog>

      <DisplayDialog
        title={`${shop.shopName} Shop Users` || 'Shop Users'}
        isOpen={showUsersTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="OTHER_USERS-table"
          data={(shopUsers ?? []) as any[]}
          filterKeys={[
            'email',
            'firstName',
            'lastName',
            'shopUserRole',
            'shopUserState',
            'userID',
            'userName',
            'userState',
          ]}
          columnDefs={[
            ...userTableColumns,
            ...(((numUsedTechCerts ?? 0) < (maxTechCerts ?? 0) ||
              (maxTechCerts ?? 0) - (numUsedTechCerts ?? 0) <= 0) &&
            user?.userType === 'ADMIN'
              ? [
                  {
                    Header: 'Actions',
                    accessor: '',
                    minWidth: 90,
                    Cell: (row: any) =>
                      row.original.certifiedTech || row.original?.idVerified ? (
                        <></>
                      ) : (maxTechCerts ?? 0) - (numUsedTechCerts ?? 0) <=
                        0 ? (
                        <HelpTooltip
                          label={'This shop has no available TechCerts!'}
                          child={
                            <button
                              className="c-btn"
                              onClick={() =>
                                handleVerifyIdButtonClick(row.original)
                              }
                              disabled={true}
                            >
                              <div className="c-btn__inner">Verify ID</div>
                            </button>
                          }
                        />
                      ) : (
                        <button
                          className="c-btn"
                          onClick={() =>
                            handleVerifyIdButtonClick(row.original)
                          }
                        >
                          <div className="c-btn__inner">Verify ID</div>
                        </button>
                      ),
                  },
                ]
              : []),
          ]}
          defaultSorted={[
            {
              id: 'userName',
              desc: false,
            },
          ]}
          savedColumnsId="otherUsersTableColumns"
          subComponentRenderer={
            isNotExternal ? LogSubcomponent : ExternalLogSubcomponent
          }
        />
      </DisplayDialog>

      <Dialog
        isOpen={showPrintModal}
        onDismiss={() => closePrintModal()}
        className="c-modal"
      >
        <button
          className="c-btn-icon c-modal__print"
          onClick={() => {
            // Open a new tab if on mobile because print won't work
            if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
              window.open(pdfUrl);
            }

            // Print the iframe if not on mobile
            else if (iframe.current) {
              iframe.current.src = pdfUrl;

              iframe.current.onload = () => {
                iframe?.current?.contentWindow?.focus();
                iframe?.current?.contentWindow?.print();
              };
            } else {
              window.open(pdfUrl);
            }
          }}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fal fa-print" />
          </div>
        </button>

        <button
          className="c-btn-icon c-modal__close"
          onClick={() => closePrintModal()}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fal fa-times" />
          </div>
        </button>

        <div className="c-modal__body">
          <div className="pdf-modal" ref={pdfModalRef}>
            <div>
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  className="pdf-container"
                  onLoadSuccess={onPrintDocumentLoadSuccess}
                >
                  {Array.from({ length: printNumPages }, (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={pdfModalRef.current?.getBoundingClientRect().width}
                    />
                  ))}
                </Document>
              ) : (
                <p>Loading PDF...</p>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* CRM Shop Orders Table */}
      <DisplayDialog
        title={`${shop.shopName} Shop Orders` || 'Shop Orders'}
        isOpen={showShopOrdersTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_ORDERS-table"
          data={shop.crmShopOrders as any[]}
          filterKeys={[
            'orderNumber',
            'title',
            'status',
            'description',
            'userName',
            'customerName',
            'createdOn',
          ]}
          columnDefs={[
            ...shopOrdersColumns,
            {
              Header: 'Actions',
              Cell: (row: any) => {
                return (
                  <>
                    <div className="hidden-pdf">
                      <InvoicePrintView
                        currentShop={shop}
                        orderData={row.original}
                        customer={shop?.crmShopCustomers?.find(
                          (customer) =>
                            customer.customerID === row.original?.customerID
                        )}
                        vehicle={shop?.crmShopCustomers
                          ?.find(
                            (customer) =>
                              customer.customerID === row.original?.customerID
                          )
                          ?.vehicles?.find(
                            (vehicle) =>
                              vehicle.vehicleID === row.original?.vehicleID
                          )}
                      />
                      <iframe
                        id="pdfFrame"
                        style={{ display: 'none' }}
                        ref={iframe}
                      />
                    </div>
                    <button
                      className="c-btn-icon c-modal-slider__print"
                      onClick={() => openPrintModal(row.original)}
                    >
                      <div className="c-btn__inner" title="View PDF">
                        <i className="c-btn__icon fal fa-print" />
                      </div>
                    </button>
                  </>
                );
              },
            },
          ]}
          defaultSorted={[
            {
              id: 'createdOn',
              desc: true,
            },
          ]}
          savedColumnsId="shopOrdersColumns"
        />
      </DisplayDialog>

      {/* CRM Shop Services Table */}
      <DisplayDialog
        title={`${shop.shopName} Shop Services` || 'Shop Services'}
        isOpen={showShopServicesTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_SERVICES-table"
          data={shop.crmShopServices as any[]}
          filterKeys={['title', 'description', 'createdOn']}
          columnDefs={shopServicesColumns}
          defaultSorted={[
            {
              id: 'createdOn',
              desc: true,
            },
          ]}
          savedColumnsId="shopServicesColumns"
        />
      </DisplayDialog>

      {/* CRM Shop Parts Table */}
      <DisplayDialog
        title={`${shop.shopName} Shop Parts` || 'Shop Parts'}
        isOpen={showShopPartsTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_PARTS-table"
          data={shop.crmShopParts as any[]}
          filterKeys={['title', 'description', 'createdOn']}
          columnDefs={shopPartsColumns}
          defaultSorted={[
            {
              id: 'createdOn',
              desc: true,
            },
          ]}
          savedColumnsId="shopPartsColumns"
        />
      </DisplayDialog>

      {/* CRM Shop Customers Table */}
      <DisplayDialog
        title={`${shop.shopName} Shop Customers` || 'Shop Customers'}
        isOpen={showShopCustomersTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_CUSTOMERS-table"
          data={shop.crmShopCustomers as any[]}
          filterKeys={['firstName', 'lastName', 'phoneNumber']}
          columnDefs={shopCustomersColumns}
          defaultSorted={[
            {
              id: 'firstName',
              desc: false,
            },
          ]}
          savedColumnsId="shopCustomersColumns"
          subComponentRenderer={CustomerVehiclesSubComponent}
        />
      </DisplayDialog>

      {/* CRM Shop Events Table */}
      <DisplayDialog
        title={`${shop.shopName} Shop Events` || 'Shop Events'}
        isOpen={showShopEventsTableModal}
        onDismiss={handleHideTableModal}
        is90={true}
      >
        <GenericTable
          id="SHOP_EVENTS-table"
          data={shop.crmShopEvents as any[]}
          filterKeys={[
            'title',
            'description',
            'eventType',
            'highlighterColor',
            'startDate',
            'endDate',
          ]}
          columnDefs={shopEventsColumns}
          defaultSorted={[
            {
              id: 'startDate',
              desc: true,
            },
          ]}
          savedColumnsId="shopCustomersColumns"
        />
      </DisplayDialog>

      <TransactionsTableDialog
        supportUser={user}
        currentShop={shop}
        isOpen={showTransactionsTableModal}
        onDismiss={handleHideTableModal}
        config={config}
        oemInfo={oemInfo}
        handleRefundTransaction={handleRefundTransaction}
      />

      <Dialog
        isOpen={showVerifyIdModal}
        className="c-modal"
        aria-label={'Verify ID'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={closeVerifyIdModal}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Verify ID</h1>

        <div className="c-modal__body">
          <p>
            {`Enter needed information below from ${selectedShopUser?.firstName} ${selectedShopUser?.lastName}'s (${selectedShopUser?.userName}) ID to "verify" ID`}
          </p>

          <div className="u-flex-row l-flex-between u-margin-bottom-large">
            <div className="c-field u-width-50-percent u-margin-bottom-small">
              <label htmlFor="firstName" className="c-field__label">
                First Name:
              </label>
              <input
                id={'firstName'}
                placeholder={'First Name'}
                value={shopUserFirstName}
                onChange={(e) => {
                  setShopUserFirstName(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-50-percent u-margin-bottom-small">
              <label htmlFor="lastName" className="c-field__label">
                Last Name:
              </label>
              <input
                id={'lastName'}
                placeholder={'Last Name'}
                value={shopUserLastName}
                onChange={(e) => {
                  setShopUserLastName(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
          </div>

          <div className="l-flex-column u-padding-right-5px">
            <div className="c-field u-width-100-percent u-margin-bottom-small">
              <label htmlFor="streetAddress1" className="c-field__label">
                Street Address 1:
              </label>
              <input
                id={'streetAddress1'}
                placeholder={'Street Address 1'}
                value={shopUserStreetAddress1}
                onChange={(e) => {
                  setShopUserStreetAddress1(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-100-percent u-margin-bottom-small">
              <label htmlFor="streetAddress2" className="c-field__label">
                Street Address 2:
              </label>
              <input
                id={'streetAddress2'}
                placeholder={'Street Address 2'}
                value={shopUserStreetAddress2}
                onChange={(e) => {
                  setShopUserStreetAddress2(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
          </div>

          <div className="u-flex-row l-flex-between">
            <div className="c-field u-width-70-percent u-margin-bottom-small">
              <label htmlFor="city" className="c-field__label">
                City:
              </label>
              <input
                id={'city'}
                placeholder={'City'}
                value={shopUserCity}
                onChange={(e) => {
                  setShopUserCity(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-30-percent u-margin-bottom-small">
              <label htmlFor="zipCode" className="c-field__label">
                ZIP Code:
              </label>
              <input
                id={'zipCode'}
                placeholder={'ZIP Code'}
                value={shopUserZip}
                onChange={(e) => {
                  setShopUserZip(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
          </div>

          <div className="u-flex-row l-flex-between u-margin-bottom-large">
            <div className="c-field u-width-50-percent u-margin-bottom-small">
              <label htmlFor="state" className="c-field__label">
                State:
              </label>
              <input
                id={'state'}
                placeholder={'State'}
                value={shopUserState}
                onChange={(e) => {
                  setShopUserState(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-50-percent u-margin-bottom-small">
              <label htmlFor="country" className="c-field__label">
                Country:
              </label>
              <input
                id={'country'}
                placeholder={'Country'}
                value={shopUserCountry}
                onChange={(e) => {
                  setShopUserCountry(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
          </div>

          <div className="l-flex-column u-padding-right-5px u-margin-bottom-large">
            <div className="c-field u-width-100-percent u-margin-bottom-small">
              <label htmlFor="documentNumber" className="c-field__label">
                Document Number:
              </label>
              <input
                id={'documentNumber'}
                placeholder={'Document Number'}
                value={shopUserDocumentNumber}
                onChange={(e) => {
                  setShopUserDocumentNumber(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-100-percent u-margin-bottom-small">
              <label htmlFor="documentType" className="c-field__label">
                Document Type
              </label>
              <input
                id={'documentType'}
                placeholder={'Document Type'}
                value={shopUserDocumentType}
                onChange={(e) => {
                  setShopUserDocumentType(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
            </div>
            <div className="c-field u-width-100-percent u-margin-bottom-small">
              <label htmlFor="expirationDate" className="c-field__label">
                Expiration Date (YYYY-MM-DD)
              </label>
              <input
                id={'expirationDate'}
                placeholder={'Expiration Date'}
                value={shopUserDocumentExpirationDate}
                onChange={(e) => {
                  setShopUserDocumentExpirationDate(e.target.value);
                }}
                className={'u-padding-sides-2rem u-height-5rem'}
              />
              {!isValidYearMonthDayDate(shopUserDocumentExpirationDate) && (
                <label
                  htmlFor="expirationDate"
                  className="c-field__label u-text-error"
                >
                  Invalid Expiration Date! Enter a date using YYYY-MM-DD format.
                </label>
              )}
            </div>
          </div>

          <LoaderButton
            onClick={() => {
              handleVerifyId();
            }}
            className="c-btn"
            isLoading={isLoading}
            disabled={
              !shop?.shopID ||
              !selectedShopUser?.userID ||
              !shopUserFirstName ||
              !shopUserLastName ||
              !shopUserStreetAddress1 ||
              !shopUserCity ||
              !shopUserZip ||
              !shopUserState ||
              !shopUserCountry ||
              !shopUserDocumentNumber ||
              !shopUserDocumentType ||
              !shopUserDocumentExpirationDate ||
              !isValidYearMonthDayDate(shopUserDocumentExpirationDate)
            }
            text="Submit"
            loadingText="Submitting..."
          />
        </div>
      </Dialog>

      <AlertModal
        title={alertTitle}
        message={alertMessage}
        showModal={showAlertModal}
        size="small"
        handleCancel={() => handleCancelAlert()}
      />
    </Accordion>
  );
};

export default ShopInfoAccordion;
